/**
 * MCQ Set — Editor (Content / Questions / Configuration tabs)
 */

import * as React from 'react';
import {
    Badge,
    cn,
    Input,
    Label,
    createEmptyDocument,
} from '@ai-content-hub/content-kit';
import {
    type EditTabDescriptor,
    type CompositeQuestionsSidebarRow,
    generateId,
    type ContentEditProps,
    CompositeEditShell,
    CompositeEditContentTab,
    CompositeEditSortableQuestionsLayout,
    orderItemsByQuestionsSidebarRows,
    persistQuestionsSidebarRows,
    reconcileQuestionsSidebarRows,
    resolvedQuestionsSidebarRows,
    useLocale,
} from '@ai-content-hub/content-core';
import {
    MCQEdit,
    BlockEditor,
    createDefaultBlockContent,
    getQuestionText,
    getCompositeQuestionType,
} from '@ai-content-hub/content-elements';
import type { BlockContent, BlockContentType } from '@ai-content-hub/content-elements';
import type { MCQSetConfig, MCQSetData, MCQSetItem } from './schema';
import { createMcqSetItem, defaultMCQSetData } from './schema';
import { FileText, HelpCircle, Settings } from 'lucide-react';
import map from 'lodash/map';

export type McqSetEditProps = ContentEditProps<MCQSetData, MCQSetConfig> & {
    className?: string;
};

export const McqSetEdit: React.FC<McqSetEditProps> = ({
    value: data,
    onChange,
    disabled = false,
    className,
    configSlot,
    config,
    language,
}) => {
    const { t, language: activeLanguage } = useLocale({
        config: config?.i18n,
        initialLanguage: language,
    });
    const [activeTab, setActiveTab] = React.useState<'content' | 'questions' | 'config'>('content');

    const safeData: MCQSetData = {
        ...defaultMCQSetData,
        ...data,
        description: data?.description ?? defaultMCQSetData.description,
        items: data?.items ?? [],
    };

    const items = safeData.items;

    const [selectedId, setSelectedId] = React.useState<string | null>(null);
    const [selectedSectionId, setSelectedSectionId] = React.useState<string | null>(null);

    // EAA-2083: only the exercise selection follows `items`; the section selection is left
    // alone. Every sidebar-rows change (typing a section title included) rebuilds `items`
    // as a new array, so with no exercises yet this effect cleared the selected section on
    // the first keystroke and closed its panel. A section that is really removed is
    // deselected by CompositeEditSortableQuestionsLayout itself.
    React.useEffect(() => {
        if (items.length === 0) {
            setSelectedId(null);
            return;
        }
        setSelectedId((prev) => {
            if (prev && items.some((i) => i.id === prev)) return prev;
            return items[0].id;
        });
    }, [items]);

    const updateData = (updates: Partial<MCQSetData>) => {
        onChange({
            ...safeData,
            ...updates,
        });
    };

    const addItem = () => {
        if (disabled) return;
        const newItem = createMcqSetItem(generateId());
        const nextItems = [...items, newItem];
        updateData({
            items: nextItems,
            questionsSidebarRows: persistQuestionsSidebarRows(
                reconcileQuestionsSidebarRows(
                    safeData.questionsSidebarRows,
                    map(nextItems, (i) => i.id),
                ),
            ),
        });
        setSelectedId(newItem.id);
        setSelectedSectionId(null);
        setActiveTab('questions');
    };

    const removeItem = (itemId: string) => {
        if (disabled) return;
        const nextItems = items.filter((i) => i.id !== itemId);
        updateData({
            items: nextItems,
            questionsSidebarRows: persistQuestionsSidebarRows(
                reconcileQuestionsSidebarRows(
                    safeData.questionsSidebarRows,
                    map(nextItems, (i) => i.id),
                ),
            ),
        });
    };

    const updateItem = (itemId: string, next: MCQSetItem) => {
        if (disabled) return;
        updateData({
            items: map(items, (it) => (it.id === itemId ? next : it)),
        });
    };

    const handleQuestionsSidebarRowsChange = (rows: CompositeQuestionsSidebarRow[]) => {
        if (disabled) return;
        const nextItems = orderItemsByQuestionsSidebarRows(items, rows);
        updateData({
            items: nextItems,
            questionsSidebarRows: persistQuestionsSidebarRows(rows),
        });
    };

    const handleAddSidebarSection = () => {
        if (disabled) return;
        const sid = generateId();
        const base = resolvedQuestionsSidebarRows(
            safeData.questionsSidebarRows,
            map(items, (i) => i.id),
        );
        handleQuestionsSidebarRowsChange([
            {
                kind: 'section',
                section: { id: sid, title: '', description: createEmptyDocument() },
            },
            ...base,
        ]);
        setSelectedSectionId(sid);
        setSelectedId(null);
    };

    const sidebarRows = React.useMemo(
        () => resolvedQuestionsSidebarRows(safeData.questionsSidebarRows, map(items, (i) => i.id)),
        [items, safeData.questionsSidebarRows],
    );

    const mcqDef = getCompositeQuestionType('mcq');

    const descriptionContent: BlockContent = safeData.description ?? {
        type: 'text',
        text: '',
        variant: 'body',
        alignment: 'left',
    };

    const handleDescriptionTypeChange = (type: BlockContentType) => {
        const currentType = safeData.description?.type ?? 'text';
        if (currentType === type) return;
        updateData({ description: createDefaultBlockContent(type) });
    };

    const handleDescriptionContentChange = (content: BlockContent) => {
        updateData({ description: content });
    };

    const tabs: EditTabDescriptor[] = [
        { id: 'content', label: t('contentTab'), icon: FileText },
        { id: 'questions', label: t('questionsTab'), icon: HelpCircle, badge: items.length },
        ...(configSlot ? [{ id: 'config', label: t('configTab'), icon: Settings }] : []),
    ];

    const addQuestionOptions = [
        {
            id: 'mcq',
            label: mcqDef?.label ?? t('multipleChoice'),
        },
    ];

    const itemHasValidationError = (index: number): boolean => {
        const item = items[index];
        if (!item?.data?.choices || item.data.choices.length < 2) return false;
        return !item.data.choices.some((c) => c.isCorrect);
    };

    return (
        <CompositeEditShell
            prefix="mcq-set-edit"
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={(id) => setActiveTab(id as 'content' | 'questions' | 'config')}
            configSlot={configSlot}
            className={cn('mcq-set-edit h-full min-h-0', className)}
            renderPanel={(tabId) => {
                if (tabId === 'content') {
                    return (
                        <CompositeEditContentTab
                            prefix="mcq-set-edit"
                            disabled={disabled}
                            meta={
                                <div className="mcq-set-edit-content-meta space-y-6">
                                    <div className="space-y-2">
                                        <Label htmlFor="mcq-set-title">
                                            {t('titleLabel')}
                                            <span className="text-destructive"> *</span>
                                        </Label>
                                        <Input
                                            id="mcq-set-title"
                                            value={safeData.title ?? ''}
                                            onChange={(e) => updateData({ title: e.target.value })}
                                            placeholder={t('titlePlaceholder')}
                                            disabled={disabled}
                                            className="mcq-set-edit-title-input text-lg font-semibold"
                                        />
                                    </div>
                                    <div className="mcq-set-edit-description space-y-2">
                                        <BlockEditor
                                            layout="oneline"
                                            content={descriptionContent}
                                            onChange={handleDescriptionContentChange}
                                            onTypeChange={handleDescriptionTypeChange}
                                            disabled={disabled}
                                            label={t('descriptionLabel')}
                                            placeholder={t('enterDescription')}
                                        />
                                    </div>
                                </div>
                            }
                            page={null}
                            pageClassName="hidden !border-0 !pt-0 !min-h-0"
                        />
                    );
                }
                if (tabId === 'questions') {
                    return (
                        <CompositeEditSortableQuestionsLayout
                            prefix="mcq-set-edit"
                            listTitle={t('questionsListTitle')}
                            emptyMessage={t('emptyQuestionsMsg')}
                            items={items}
                            selectedId={selectedId}
                            onSelectId={setSelectedId}
                            selectedSectionId={selectedSectionId}
                            onSelectSectionId={setSelectedSectionId}
                            disabled={disabled}
                            minItemsToKeep={0}
                            sidebarRows={sidebarRows}
                            onSidebarRowsChange={handleQuestionsSidebarRowsChange}
                            onAddSidebarSection={handleAddSidebarSection}
                            addSidebarSectionLabel={t('addSection')}
                            removeSidebarSectionLabel={t('removeSection')}
                            onRemoveItem={removeItem}
                            itemHasValidationError={itemHasValidationError}
                            getItemLabel={(_itemId, index) =>
                                `${index + 1}. ${getQuestionText(items[index]?.data) || t('untitledQuestion')}`
                            }
                            addButtonLabel={t('addQuestion')}
                            addButtonOptions={addQuestionOptions}
                            addButtonClassName="mcq-set-edit-add"
                            onAddItem={() => addItem()}
                            sectionDetailTitle={t('sectionDetailTitle')}
                            detailTitle={t('detailTitle')}
                            detailBadge={
                                <Badge
                                    variant="outline"
                                    className="border-success/30 bg-success/10"
                                >
                                    {mcqDef?.label ?? t('multipleChoice')}
                                </Badge>
                            }
                            renderItemDetail={({ itemId }) => {
                                const rowItem = items.find((i) => i.id === itemId);
                                if (!rowItem) {
                                    return null;
                                }
                                return (
                                    <MCQEdit
                                        value={rowItem.data}
                                        onChange={(d) =>
                                            updateItem(rowItem.id, {
                                                ...rowItem,
                                                data: d,
                                            })
                                        }
                                        config={config}
                                        disabled={disabled}
                                        language={activeLanguage}
                                    />
                                );
                            }}
                            detailContent={null}
                            detailEmptyMessage={t('detailEmptyMessage')}
                            removeItemLabel={t('removeQuestion')}
                            richTextTranslate={t}
                        />
                    );
                }
                return null;
            }}
        />
    );
};

export default McqSetEdit;
